import { useEffect, useRef, useState } from "react";
import { Link2, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const LoanRow = ({ x, result }) => {
  const outcome = result?.outcome || (x.eligible ? "Will send" : "Skip");
  const reason = result ? result.reason : x.skip_reason;
  const tone = outcome === "Sent" || outcome === "Will send" ? "text-emerald-700" : outcome === "Failed" ? "text-destructive" : "text-muted-foreground";
  return (
    <li className="flex items-start justify-between gap-3 py-2.5 text-sm" data-testid={`bulk-row-${x.loan_number}`}>
      <div className="min-w-0">
        <div className="font-medium">{x.borrower_name} <span className="mono text-xs text-muted-foreground">{x.loan_number}</span></div>
        {reason && <div className="text-xs text-muted-foreground" data-testid={`bulk-reason-${x.loan_number}`}>{reason}</div>}
      </div>
      <div className="shrink-0 text-right">
        <div className="num font-semibold">{money(x.amount_due_cents)}</div>
        <div className={`text-xs font-semibold ${tone}`} data-testid={`bulk-outcome-${x.loan_number}`}>{outcome}</div>
      </div>
    </li>
  );
};

export const BulkPayLinksButton = ({ lateCount, onDone }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const key = useRef("");

  useEffect(() => {
    if (!open) return;
    key.current = newKey();
    setPreview(null); setResult(null); setErr("");
    api.get("/dashboard/bulk-pay-links").then((r) => setPreview(r.data)).catch((e) => setErr(apiError(e).message));
  }, [open]);

  const confirm = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/dashboard/bulk-pay-links", { idempotency_key: key.current });
      setResult(data);
      onDone?.();
    } catch (e) {
      setErr(apiError(e).message);
    } finally {
      setBusy(false);
    }
  };

  const byLoan = Object.fromEntries((result?.items || []).map((r) => [r.loan_id, r]));
  const rows = result ? result.items : preview?.items || [];
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} disabled={!lateCount} data-testid="bulk-pay-links-button">
        <Link2 /> Send pay links to late loans ({lateCount ?? 0})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-pay-links-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Send pay links to late loans</DialogTitle>
            <DialogDescription>
              {result ? "Done. Each sent link is logged on its loan." : preview ? `${preview.eligible} of ${preview.count} late loans will get a fresh 7-day pay link.` : "Checking late loans…"}
            </DialogDescription>
          </DialogHeader>
          {result && <div className="rounded-md bg-muted/50 px-4 py-3 text-sm font-semibold" data-testid="bulk-summary">{result.summary}</div>}
          {!preview && !err && <div className="py-6 text-center"><Loader2 className="mx-auto animate-spin" /></div>}
          <ul className="max-h-80 divide-y overflow-y-auto" data-testid="bulk-list">
            {rows.map((x) => <LoanRow key={x.loan_id} x={x} result={byLoan[x.loan_id]} />)}
          </ul>
          {err && <p className="text-sm text-destructive" data-testid="bulk-error">{err}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="bulk-close-button">{result ? "Close" : "Cancel"}</Button>
            {!result && (
              <Button onClick={confirm} disabled={busy || !preview?.eligible} data-testid="bulk-confirm-button">
                {busy && <Loader2 className="animate-spin" />} Send {preview?.eligible ?? 0} pay link{preview?.eligible === 1 ? "" : "s"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
